import clsx from "clsx";
import { Popover } from "radix-ui";
import * as React from "react";
import styles from "./popover.module.scss";

// --- Root Component (manages state) ---
export type PopoverProps = React.ComponentPropsWithoutRef<typeof Popover.Root>;
const PopoverRoot: React.FC<PopoverProps> = ({ ...props }) => (
  <Popover.Root {...props} />
);
PopoverRoot.displayName = "Popover";

// --- Trigger Component (opens the popover) ---
export type PopoverTriggerProps = React.ComponentPropsWithoutRef<
  typeof Popover.Trigger
>;
const PopoverTrigger = React.forwardRef<
  React.ElementRef<typeof Popover.Trigger>,
  PopoverTriggerProps
>(({ className, asChild, ...props }, ref) => (
  <Popover.Trigger
    ref={ref}
    className={clsx(className)}
    asChild={asChild}
    {...props}
  />
));
PopoverTrigger.displayName = Popover.Trigger.displayName;

// --- Anchor Component (optional, for positioning relative to an element other than Trigger) ---
export type PopoverAnchorProps = React.ComponentPropsWithoutRef<
  typeof Popover.Anchor
>;
const PopoverAnchor = React.forwardRef<
  React.ElementRef<typeof Popover.Anchor>,
  PopoverAnchorProps
>(({ className, ...props }, ref) => (
  <Popover.Anchor ref={ref} className={clsx(className)} {...props} />
));
PopoverAnchor.displayName = Popover.Anchor.displayName;

// --- Portal Component (renders content into document.body or specified container) ---
const PopoverPortal = Popover.Portal;

// --- Content Component (the popover window) ---
export type PopoverContentProps = React.ComponentPropsWithoutRef<
  typeof Popover.Content
>;
const PopoverContent = React.forwardRef<
  React.ElementRef<typeof Popover.Content>,
  PopoverContentProps
>(
  (
    {
      className,
      align = "center",
      side = "bottom",
      sideOffset = 4,
      alignOffset = 0,
      avoidCollisions = true,
      children,
      ...props
    },
    ref
  ) => (
    <Popover.Portal>
      <Popover.Content
        ref={ref}
        align={align}
        side={side}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        avoidCollisions={avoidCollisions}
        className={clsx(styles.content, className)}
        {...props}
      >
        {children}
      </Popover.Content>
    </Popover.Portal>
  )
);
PopoverContent.displayName = Popover.Content.displayName;

// --- Arrow Component (optional visual arrow) ---
export type PopoverArrowProps = React.ComponentPropsWithoutRef<
  typeof Popover.Arrow
>;
const PopoverArrow = React.forwardRef<
  React.ElementRef<typeof Popover.Arrow>,
  PopoverArrowProps
>(({ className, width = 10, height = 5, ...props }, ref) => (
  <Popover.Arrow
    ref={ref}
    width={width}
    height={height}
    className={clsx(styles.arrow, className)}
    {...props}
  />
));
PopoverArrow.displayName = Popover.Arrow.displayName;

// --- Close Component (closes the popover, often a button inside content) ---
export type PopoverCloseProps = React.ComponentPropsWithoutRef<
  typeof Popover.Close
>;
const PopoverClose = React.forwardRef<
  React.ElementRef<typeof Popover.Close>,
  PopoverCloseProps
>(({ className, ...props }, ref) => (
  <Popover.Close ref={ref} className={clsx(className)} {...props} />
));
PopoverClose.displayName = Popover.Close.displayName;

export {
  PopoverRoot as Popover,
  PopoverTrigger,
  PopoverAnchor,
  PopoverPortal,
  PopoverContent,
  PopoverArrow,
  PopoverClose,
};
